import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

/**
 * Per-gate timeline: events + alerts merged into one infinite-scroll feed.
 *
 * Used both in the customer's slide panel and in the admin gate-detail
 * page (per role-hierarchy rule). Picks the right URL prefix via
 * `apiBase`. The backend pages cursor-style — we hand it back the
 * timestamp of the last item we got and ask for older.
 */

const FILTERS = [
  { value: 'all',        label: 'הכל' },
  { value: 'open_close', label: 'פתיחות וסגירות' },
  { value: 'fault',      label: 'תקלות' },
  { value: 'alert',      label: 'התראות' },
];

const PAGE_SIZE = 50;

// Visual style per event kind. The `dot` colors echo the rest of the
// system (red = something to look at, yellow = warning, green = ok,
// grey = informational).
const KIND_STYLE = {
  open:                  { dot: '#22c55e', glyph: '↑' },  // gate raised
  close:                 { dot: '#6b7280', glyph: '↓' },  // gate lowered
  fault_start:           { dot: '#dc2626', glyph: '!' },
  fault_end:             { dot: '#22c55e', glyph: '✓' },
  leveler_working_start: { dot: '#ea580c', glyph: '🚚' },
  leveler_rest_start:    { dot: '#16a34a', glyph: '⏸' },
  alert_raised:          { dot: '#f59e0b', glyph: '🔔' },
  alert_read:            { dot: '#9ca3af', glyph: '✓' },
};

// Source-of-pulse chip styling — only shown for open/close events.
// Picked colours that don't clash with the dot palette above.
const TRIGGER_STYLE = {
  system:     { bg: '#dbeafe', fg: '#1e40af', label: 'מהמערכת' },
  manual:     { bg: '#ede9fe', fg: '#6d28d9', label: 'מהפיקוד' },
  auto_close: { bg: '#f1f5f9', fg: '#475569', label: 'סגירה אוטומטית' },
};


function formatDuration(ms) {
  if (ms < 60_000) return `${Math.round(ms / 1000)} שניות`;
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} דקות`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  if (remMin === 0) return `${hours} שעות`;
  return `${hours}:${String(remMin).padStart(2, '0')} שעות`;
}


function GateTimeline({ gateId, apiBase = 'customer' }) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState(null);
  const sentinelRef = useRef(null);

  // Reset and reload from the top whenever the filter or gate changes.
  useEffect(() => {
    setItems([]);
    setCursor(null);
    setHasMore(true);
    setError(null);
  }, [gateId, filter]);

  const loadMore = useCallback(async (cursorToUse) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE };
      if (cursorToUse) params.cursor = cursorToUse;
      if (filter !== 'all') params.kind = filter;
      const res = await axios.get(
        `/api/${apiBase}/gates/${gateId}/timeline`,
        { params }
      );
      const newItems = res.data.items || [];
      setItems(prev => cursorToUse ? [...prev, ...newItems] : newItems);
      setCursor(res.data.next_cursor);
      setHasMore(!!res.data.next_cursor && newItems.length > 0);
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה בטעינת ההיסטוריה');
    } finally {
      setLoading(false);
    }
  }, [gateId, apiBase, filter]);

  // First page (or after a reset).
  useEffect(() => {
    if (items.length === 0 && hasMore && !loading) {
      loadMore(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, hasMore, filter]);

  // IntersectionObserver trips when the sentinel scrolls into view —
  // that's "user reached the bottom, fetch the next page".
  useEffect(() => {
    if (!hasMore || loading) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && cursor) {
        loadMore(cursor);
      }
    }, { rootMargin: '40px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, cursor, loadMore]);

  // Compute "פתוח X דקות" hints by pairing each `close` with the
  // previous `open` (the next entry in the array, since we render
  // newest-first). Keeps the metadata local — no need to enrich the
  // server response.
  const itemsWithDurations = items.map((it, idx) => {
    if (it.kind !== 'close') return it;
    // Find the next open in chronological order = next in array
    // (since array is newest-first, the next item is older).
    for (let i = idx + 1; i < items.length; i++) {
      if (items[i].kind === 'open') {
        const ms = new Date(it.timestamp).getTime() - new Date(items[i].timestamp).getTime();
        if (ms > 0) {
          return { ...it, _duration: formatDuration(ms) };
        }
        break;
      }
    }
    return it;
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.85rem', flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            style={{
              padding: '0.3rem 0.7rem',
              border: '1px solid #e5e7eb',
              background: filter === f.value ? '#1f2937' : '#fff',
              color: filter === f.value ? '#fff' : '#374151',
              borderRadius: '999px',
              fontSize: '0.78rem',
              cursor: 'pointer',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {items.length === 0 && !loading && !error && (
        <p style={{ color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center', padding: '1.5rem 0' }}>
          {filter === 'all' ? 'אין אירועים עדיין' : 'אין אירועים מסוג זה'}
        </p>
      )}

      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {itemsWithDurations.map(it => {
          const style = KIND_STYLE[it.kind] || { dot: '#9ca3af', glyph: '·' };
          return (
            <li
              key={it.id}
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                gap: '0.6rem',
                alignItems: 'center',
                padding: '0.55rem 0',
                borderBottom: '1px solid #f3f4f6',
                fontSize: '0.85rem',
              }}
            >
              <span style={{
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                background: style.dot,
                color: '#fff',
                fontSize: '0.75rem',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}>
                {style.glyph}
              </span>
              <span style={{ color: '#1a1a2e' }}>
                {it.label}
                {it._duration && (
                  <span style={{ color: '#9ca3af', marginRight: '0.4rem', fontSize: '0.78rem' }}>
                    (פתוח {it._duration})
                  </span>
                )}
                {TRIGGER_STYLE[it.trigger] && (
                  <span style={{
                    display: 'inline-block',
                    marginRight: '0.5rem',
                    padding: '0.1rem 0.5rem',
                    borderRadius: 999,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    background: TRIGGER_STYLE[it.trigger].bg,
                    color: TRIGGER_STYLE[it.trigger].fg,
                    verticalAlign: 'middle',
                  }}>
                    {TRIGGER_STYLE[it.trigger].label}
                  </span>
                )}
              </span>
              <span style={{ color: '#9ca3af', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                {new Date(it.timestamp).toLocaleString('he-IL', {
                  timeZone: 'Asia/Jerusalem',
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </li>
          );
        })}
      </ul>

      {error && (
        <div style={{ color: '#dc2626', fontSize: '0.85rem', textAlign: 'center', padding: '0.75rem 0' }}>
          {error}
        </div>
      )}

      {hasMore && (
        <div ref={sentinelRef} style={{ height: '24px' }}>
          {loading && (
            <p style={{ color: '#9ca3af', fontSize: '0.78rem', textAlign: 'center', margin: '0.5rem 0' }}>
              טוען עוד...
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default GateTimeline;
